<script setup lang="ts">
import { useBreadcrumbSchema } from "~/composables/useBreadcrumbSchema";
import { BreadcrumbRoute } from "~/types/breadcrumb-routes";

// --- Content interfaces ---
interface MetaTag {
  hid?: string;
  name?: string;
  content: string;
}

interface MetaTags {
  title: string;
  meta: MetaTag[];
}

interface HeroSection {
  heading: string;
  subcopy: string;
  ctaText: string;
  imageSrc: string;
  lightImageSrc: string;
  width: string;
  height: string;
}

interface HeroStruggle {
  label: string;
  svgIcon: string;
}

interface StrugglesSection {
  heading: string;
  subcopy: string;
  struggles: HeroStruggle[];
  ctaText: string;
}

interface CapabilityCard {
  svgIcon: string;
  title: string;
  desc: string;
}

interface CapabilityCardsSection {
  headingLine1: string;
  headingLine2: string;
  subcopy: string;
  cards: CapabilityCard[];
  ctaText: string;
}

interface ScenarioCard {
  title: string;
  problem: string;
  solution: string;
  result: string;
}

interface ScenariosSection {
  heading: string;
  subcopy: string;
  cards: ScenarioCard[];
}

interface ServiceContentSection {
  heading: string;
  subheading: string;
  text: string[];
}

interface FeatureCard {
  number: string;
  title: string;
  description: string;
  svgIcon?: string;
}

interface FeaturesSection {
  heading: string;
  subcopy: string;
  svgIcons: string[];
  cards: FeatureCard[];
  ctaText: string;
}

interface ProcessStep {
  id: string;
  title: string;
  description: string;
}

interface ProcessStepsSection {
  heading: string;
  subcopy: string;
  steps: ProcessStep[];
  ctaText: string;
}

interface BenefitCard {
  svgIcon: string;
  title: string;
  description: string;
}

interface BenefitCardsSection {
  heading: string;
  subcopy: string;
  cards: BenefitCard[];
}

interface OverlayCard {
  title: string;
  description: string;
}

interface OverlayPosition {
  top: string;
  left: string;
  titleWidth?: string;
  descWidth?: string;
}

interface OverlaySection {
  heading: string;
  subcopy: string;
  cards: OverlayCard[];
  overlayPositions: OverlayPosition[];
}

interface QuoteSection {
  text: string;
  ctaText: string;
}

interface FaqItem {
  title: string;
  description: string;
  tags: string[];
}

interface FaqSection {
  heading: string;
  suffix: string;
  subtext: string;
  filterTags: string[];
  ctaText: string;
  items: FaqItem[];
}

interface UmbracoContent {
  meta_tags: MetaTags;
  hero: HeroSection;
  struggles: StrugglesSection;
  capabilityCards: CapabilityCardsSection;
  scenarios: ScenariosSection;
  service_content: ServiceContentSection;
  features: FeaturesSection;
  processSteps: ProcessStepsSection;
  benefitCards: BenefitCardsSection;
  overlay: OverlaySection;
  quote: QuoteSection;
  faq: FaqSection;
}

// --- Fetch content ---
const url = useRequestURL();
const { data, error } = await useFetch(
  `${url.origin}/content/services/cms-development/umbraco-development-content.json`
);

if (error.value || !data.value) {
  throw createError({
    statusCode: 404,
    statusMessage: "Page not found",
  });
}

const content = data.value as UmbracoContent;
const {
  meta_tags,
  hero,
  struggles,
  capabilityCards,
  scenarios,
  service_content,
  features,
  processSteps,
  benefitCards,
  overlay,
  quote,
  faq,
} = content;

definePageMeta({
  layout: "services-layout",
});

const breadcrumbs = useBreadcrumbSchema(BreadcrumbRoute.Services);

useHead({
  htmlAttrs: { lang: 'en' },
  title: meta_tags.title,
  meta: [
    ...meta_tags.meta.map((m: MetaTag) => ({ name: m.name, content: m.content, hid: m.hid })),
    { name: "robots", content: "index, follow" },
    { property: "og:title", content: meta_tags.title },
    {
      property: "og:description",
      content:
        meta_tags.meta.find((m: MetaTag) => m.name === "description")?.content ||
        hero.subcopy ||
        "",
    },
  ],
  link: [
    {
      rel: "preload",
      as: "image",
      href: hero.imageSrc,
    },
  ],
  script: [
    {
      type: "application/ld+json",
      innerHTML: JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: hero.heading,
            serviceType: hero.heading,
            description: hero.subcopy,
            provider: {
              "@type": "Organization",
              name: "WebReinvent",
              url: "https://webreinvent.com",
            },
            url: `https://webreinvent.com${useRoute().path}`,
          },
          {
            "@type": "FAQPage",
            mainEntity: faq.items.map((item: FaqItem) => ({
              "@type": "Question",
              name: item.title,
              acceptedAnswer: {
                "@type": "Answer",
                text: item.description,
              },
            })),
          },
          ...(breadcrumbs.value ? [breadcrumbs.value] : []),
        ],
      }),
    },
  ],
});

const visible = ref(false);

const openModal = () => {
  visible.value = true;
};

// --- Component imports ---
import WrDesignHeroCard from "~/components/molecules/WrDesignHeroCard.vue";

const WrDesignStrugglesSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignStrugglesSection.vue"),
);
const WrDesignProcessCardsGrid = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignProcessCardsGrid.vue"),
);
const WrDesignScenarioCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignScenarioCards.vue"),
);
const WrWhoAreWeCard = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrWhoAreWeCard.vue"),
);
const WrDesignIconLineDecorativeSquaresCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignIconLineDecorativeSquaresCards.vue"),
);
const WrDesignStaggeredProcessCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignStaggeredProcessCards.vue"),
);
const WrDesignBenefitCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignBenefitCards.vue"),
);
const WrDesignOverlaySvgCards = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignOverlaySvgCards.vue"),
);
const WrDesignQuoteCtaSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignQuoteCtaSection.vue"),
);
const WrDesignFaqsSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrDesignFaqsSection.vue"),
);
const WrSelectedProject = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrSelectedProject.vue"),
);
const WrOurServiceSection = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrOurServiceSection.vue"),
);
const WrContactFormPopup = defineLazyHydrationComponent(
  "visible",
  () => import("~/components/molecules/WrContactFormPopup.vue"),
);

// --- Computed mappings ---
const staggeredSteps = computed(() =>
  processSteps.steps.map((step) => ({
    number: step.id,
    title: step.title,
    desc: step.description,
  }))
);

const overlayCards = computed(() =>
  overlay.cards.map((card) => ({
    title: card.title,
    description: card.description,
  }))
);

const capabilityProcessCards = computed(() =>
  capabilityCards.cards.map((card) => ({
    title: card.title,
    description: card.desc,
    svgIcon: card.svgIcon,
  }))
);
</script>

<template>
  <!-- ==================== HERO SECTION ==================== -->
  <div class="max-w-screen-xl px-4 mx-auto pt-32">
    <WrDesignHeroCard
      :heading="hero.heading"
      :subcopy="hero.subcopy"
      :ctaText="hero.ctaText"
      :imageSrc="hero.imageSrc"
      :lightImageSrc="hero.lightImageSrc"
      :width="hero.width"
      :height="hero.height"
      @cta-clicked="openModal"
    />
  </div>

  <!-- ==================== STRUGGLES SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignStrugglesSection
      :heading="struggles.heading"
      :subcopy="struggles.subcopy"
      :struggles="struggles.struggles"
      :ctaText="struggles.ctaText"
      bgPosition="50% -240px"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== CAPABILITY CARDS ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignProcessCardsGrid
      :heading="capabilityCards.headingLine1 + ' ' + capabilityCards.headingLine2"
      :subcopy="capabilityCards.subcopy"
      :cards="capabilityProcessCards"
      :ctaText="capabilityCards.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== SCENARIO CARDS ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignScenarioCards
      :heading="scenarios.heading"
      :subcopy="scenarios.subcopy"
      :cards="scenarios.cards"
    />
  </section>

  <!-- ==================== SERVICE CONTENT ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto my-20 relative z-10">
    <WrWhoAreWeCard
      :heading="service_content.heading"
      :subheading="service_content.subheading"
      :content="service_content.text"
    />
  </section>

  <!-- ==================== FEATURES (Icon Line Decorative Squares) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignIconLineDecorativeSquaresCards
      :heading="features.heading"
      :subcopy="features.subcopy"
      :cards="features.cards"
      :ctaText="features.ctaText"
      @cta-clicked="openModal"
    >
      <template v-for="(card, i) in features.cards" :key="i" #[`icon-${i}`]>
        <WrIcon
          :name="features.svgIcons?.[i] || card.svgIcon || ''"
          class="size-8 text-[#613FAA] dark:text-white"
        />
      </template>
    </WrDesignIconLineDecorativeSquaresCards>
  </section>

  <!-- ==================== PROCESS STEPS (Staggered) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignStaggeredProcessCards
      :heading="processSteps.heading"
      :subcopy="processSteps.subcopy"
      :steps="staggeredSteps"
      :ctaText="processSteps.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BENEFIT CARDS ==================== -->
  <div class="max-w-screen-xl px-4 mx-auto">
    <WrDesignBenefitCards
      :heading="benefitCards.heading"
      :subcopy="benefitCards.subcopy"
      :cards="benefitCards.cards"
    />
  </div>

  <!-- ==================== WHY CHOOSE US (Overlay SVG Cards) ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignOverlaySvgCards
      :heading="overlay.heading"
      :subcopy="overlay.subcopy"
      :cards="overlayCards"
      :overlayPositions="overlay.overlayPositions"
    />
  </section>

  <!-- ==================== QUOTE + CTA ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto">
    <WrDesignQuoteCtaSection
      :text="quote.text"
      :ctaText="quote.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== FAQ SECTION ==================== -->
  <section class="max-w-screen-xl px-4 mx-auto mt-16 lg:mt-[100px]">
    <WrDesignFaqsSection
      :heading="faq.heading"
      :suffix="faq.suffix"
      :subtext="faq.subtext"
      :filterTags="faq.filterTags"
      :faqItems="faq.items"
      :ctaText="faq.ctaText"
      @cta-clicked="openModal"
    />
  </section>

  <!-- ==================== BOTTOM SECTIONS ==================== -->
  <section
    class="relative z-10 max-w-screen-xl lg:-mt-44 lg:pb-28 overflow-visible grid place-items-center order-9"
  >
    <div class="max-w-screen-lg mx-auto px-4 w-full relative">
      <div
        class="hidden dark:block opacity-30 size-[764px] rounded-full bg-[linear-gradient(136.28deg,_rgba(51,4,79,0)_30.43%,_#260049_54.53%,_#36006F_92.37%)] absolute -top-[30%] -left-[67%]"
      ></div>
      <div
        class="hidden dark:block opacity-50 size-[190px] rounded-full bg-[var(--bg-circle-top-light)] absolute -bottom-[15%] -right-80"
      ></div>
    </div>
  </section>

  <section class="section-spacing">
    <div class="container-wrapper-lg">
      <WrSelectedProject />
    </div>
  </section>

  <WrOurServiceSection
    title="Our Capabilities"
    description="One of our latest add-on skills from the newest technologies."
  />

  <section
    class="relative section-spacing dark:bg-none overflow-hidden z-20"
  >
    <div class="container-wrapper-xl">
      <h4 class="text-center mb-11">
        <WrGradientText
          text="Trusted for excellence and efficiency"
          append-class="text-3xl lg:text-[48px]"
        />
        <p class="text-lg text-[var(--foreground)] font-anvtgrade-book">
          WebReinvent is trusted by leading organisations to enhance their
          development and operational efficiency.
        </p>
      </h4>
      <WrTestimonialCardCarousel class="mb-12" />
      <WrButton link="/testimonials" container-class="max-w-max mx-auto mt-7"
        >View All</WrButton
      >
    </div>
  </section>

  <WrContactFormPopup :isOpen="visible" @close="visible = false" class="z-30" />
</template>

<style scoped>
:deep(.hero-image-wrapper) {
  max-width: 500px;
}
</style>
